import { yupResolver } from '@hookform/resolvers/yup';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Position } from '@staffedupusa/shared-components';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import {
  checkBoxOptions,
  jobPostingStatuses,
  payFrequencyTypes,
  SubmitJobPosting,
  updatedPositionTypes,
} from '@features/jobPostings';
import {
  emojiWarningMessage,
  PositionOption,
  useGetPositions,
} from '@features/positions';
import RequestPositionModal from '@features/positions/components/RequestPositionModal/RequestPositionModal';
import { useAddTemplateJobPosting } from '../hooks/useAddTemplateJobPosting';
import { useUpdateTemplateJobPosting } from '../hooks/useUpdateTemplateJobPosting';
import { Close, HelpOutline } from '@mui/icons-material';
import BundledEditor from '@utils/BundledEditor';
import { containsEmoji, getStrippedText } from '@utils/helper';
import NoCompensationWarningModal from '@features/jobPostings/components/NoCompensationWarningModal/NoCompensationWarningModal';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';
import AssistantIcon from '@mui/icons-material/Assistant';
import { AITooltipText } from '@features/messaging';

type TemplateJobPostingModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  jobPosting: Position | null;
  templateId: number;
  industryId: number;
};

const schema: yup.ObjectSchema<SubmitJobPosting> = yup
  .object({
    id: yup.number(),
    standard_position_id: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' ? null : Number(originalValue),
      )
      .nullable()
      .required('Position Category is required'),
    s_title: yup
      .string()
      .required('Title is required')
      .test('not-empty-html', 'Title cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    status: yup.number().nullable().required('Position Status is required'),
    s_description: yup
      .string()
      .required('Description is required')
      .test('not-empty-html', 'Description cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    s_pay_frequency: yup.string().required('Pay Frequency is required'),
    job_location: yup.number().required('Job Location is required'),
    s_type: yup.string().required('Position Type is required'),
    s_salary: yup.number().nullable(),
    s_salary_max: yup.number().nullable(),
    tips: yup.boolean(),
    commission: yup.boolean(),
    bonus: yup.boolean(),
    incentives: yup.boolean(),
    benefits: yup.boolean(),
  })
  .required();

const TemplateJobPostingModal: FC<TemplateJobPostingModalProps> = ({
  isOpen,
  handleClose,
  jobPosting,
  templateId,
  industryId,
}) => {
  const [errorText, setErrorText] = useState<string>('');
  const [options, setOptions] = useState<PositionOption[]>([]);
  const [isRequestPositionModalOpen, setIsRequestPositionModalOpen] =
    useState(false);
  const [isPayRange, setIsPayRange] = useState<boolean>(
    Boolean(jobPosting?.s_salary_max),
  );
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('');
  const [noCompensionWarning, setNoCompensionWarning] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<SubmitJobPosting>({
    resolver: yupResolver(schema),
    defaultValues: {
      standard_position_id: null,
      s_title: '',
      status: null,
      s_description: '',
      s_type: '',
      s_pay_frequency: '',
      job_location: 0,
      s_salary: null,
      s_salary_max: null,
      tips: false,
      commission: false,
      bonus: false,
      incentives: false,
      benefits: false,
    },
  });

  const descriptionContainsEmoji = containsEmoji(watch('s_description') || '');

  const { addJobPosting, isAddingJobPosting } = useAddTemplateJobPosting({
    setErrorText,
  });

  const { updateJobPosting, isUpdatingJobPosting } =
    useUpdateTemplateJobPosting({
      setErrorText,
    });

  useGetPositions({
    setPositions: setOptions,
    industryId,
  });
  const handleInsertAIContent = (content: string) => {
    setValue('s_description', content);
    setShowAiPromptModal(false);
  };

  const handleSubmitForm = async (data: SubmitJobPosting) => {
    if (!data.s_salary) {
      setNoCompensionWarning(true);
      return;
    }
    onSubmit(data);
  };

  const onSubmit = (values: SubmitJobPosting) => {
    if (isPayRange && !values.s_salary) {
      setErrorText('Please enter a minimum salary');
      return;
    }
    if (isPayRange && !values.s_salary_max) {
      setErrorText('Please enter a maximum salary');
      return;
    }
    if (values.s_salary && values.s_salary_max) {
      if (Number(values.s_salary) > Number(values.s_salary_max)) {
        setErrorText('Minimum salary cannot be greater than maximum salary');
        return;
      }
    }
    const payload = {
      ...values,
      template_id: templateId,
      ...(values.s_salary === null && { s_salary: '' }),
      ...(values.s_salary_max === null && { s_salary_max: '' }),
    };
    const requestOptions = {
      onSuccess: () => {
        handleClose();
        reset();
        toast.success('Job Posting Saved');
      },
    };
  
    return jobPosting
      ? updateJobPosting(
          { ...payload, id: jobPosting.id } as SubmitJobPosting,
          requestOptions,
        )
      : addJobPosting(payload as SubmitJobPosting, requestOptions);
  };

  useEffect(() => {
    if (jobPosting) {
      setValue('standard_position_id', jobPosting.standard_position.id);
      setValue('s_title', jobPosting.s_title);
      setValue('s_description', jobPosting.s_description);
      setValue('status', jobPosting.status);
      setValue(
        's_type',
        jobPosting.s_type && jobPosting.s_type === 'Singe Event'
          ? 'Single Event'
          : jobPosting.s_type,
      );
      if (jobPosting.s_salary) {
        setValue('s_salary', Number(jobPosting.s_salary));
      }
      if (jobPosting.s_pay_frequency) {
        setValue('s_pay_frequency', jobPosting.s_pay_frequency);
      } else {
        setValue('s_pay_frequency', '');
      }
      if (jobPosting.job_location) {
        setValue('job_location', jobPosting.job_location);
      } else {
        setValue('job_location', 0);
      }
      if (jobPosting.s_salary_max) {
        setValue('s_salary_max', Number(jobPosting.s_salary_max));
        setIsPayRange(true);
      }
      setValue('tips', jobPosting.tips);
      setValue('commission', jobPosting.commission);
      setValue('bonus', jobPosting?.bonus);
      setValue('incentives', jobPosting?.incentives);
      setValue('benefits', jobPosting?.benefits);
    } else {
      reset();
      setValue('s_pay_frequency', 'Hourly');
    }
  }, [jobPosting]);

  useEffect(() => {
    if (!isPayRange) {
      setValue('s_salary_max', null);
    }
  }, [isPayRange]);

 

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {
          handleClose();
          setIsPayRange(false);
          setErrorText('');
        }}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={() => {
              handleClose();
              setIsPayRange(false);
              setErrorText('');
            }}
          >
            <Close />
          </IconButton>
          <br />
          <Typography variant="h4" sx={{ pt: 3 }}>
            {jobPosting ? 'Edit a Position' : 'Add a Position'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {errorText && <Alert severity="error">{errorText}</Alert>}
            {descriptionContainsEmoji && (
              <Alert severity="warning">{emojiWarningMessage}</Alert>
            )}
            <form
              onSubmit={handleSubmit(handleSubmitForm)}
              style={{ marginBottom: 10 }}
            >
              <Stack
                spacing={3}
                sx={{
                  pt: 1,
                  '& p': {
                    ml: 1,
                    fontSize: '15px',
                  },
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <Controller
                    control={control}
                    name="standard_position_id"
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        value={options.find((option) => option.id === value)}
                        options={options}
                        inputValue={autocompleteInputValue}
                        sx={{ width: '100%' }}
                        onInputChange={(e, newValue) => {
                          if (newValue === "I can't find the position I need") {
                            setAutocompleteInputValue('');
                          } else {
                            setAutocompleteInputValue(newValue);
                          }
                        }}
                        onChange={(e, data: PositionOption | null) => {
                          if (data?.id === 0) {
                            return setIsRequestPositionModalOpen(true);
                          }

                          if (data) {
                            setValue('s_title', data.name, {
                              shouldValidate: true,
                              shouldTouch: true,
                              shouldDirty: true,
                            });
                            setValue('s_description', data.description ?? '');
                          }

                          onChange(data?.id ?? '');
                        }}
                        filterOptions={(opts: PositionOption[], inputState) =>
                          opts.filter(
                            (option) =>
                              option.id === 0 ||
                              option.name
                                .toLowerCase()
                                .includes(inputState.inputValue.toLowerCase()),
                          )
                        }
                        getOptionLabel={(option) => option.name || ''}
                        renderInput={(params) => (
                          <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            sx={{ width: '100%' }}
                          >
                            <TextField
                              {...params}
                              label="Position Category"
                              variant="outlined"
                              size="small"
                              fullWidth
                              error={Boolean(errors.standard_position_id)}
                              helperText={
                                errors?.standard_position_id
                                  ? errors?.standard_position_id.message
                                  : ''
                              }
                            />
                            <Tooltip
                              title="Please select a position category first. You can name your position below."
                              placement="right"
                              arrow
                            >
                              <HelpOutlineIcon />
                            </Tooltip>
                          </Stack>
                        )}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="s_title"
                    render={({ field }) => (
                      <Stack
                        spacing={2}
                        direction="row"
                        alignItems="center"
                        sx={{ width: '100%' }}
                      >
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label="Position Title"
                          hiddenLabel
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={Boolean(errors.s_title)}
                          helperText={
                            errors?.s_title ? errors?.s_title.message : ''
                          }
                        />
                        <Tooltip
                          title="Customize your position names to reflect the jobs you offer, your position categories will help them get found in the search no matter what you name them."
                          placement="right"
                          arrow
                        >
                          <HelpOutlineIcon />
                        </Tooltip>
                      </Stack>
                    )}
                  />
                </Stack>

                <Controller
                  control={control}
                  name="s_description"
                  render={({ field }) => (
                    <Stack spacing={1}>
                      <BundledEditor
                        {...field}
                        onEditorChange={(newValue: string) =>
                          field.onChange(newValue)
                        }
                        onChange={() => {}}
                        init={{
                          height: 200,
                          menubar: false,
                          plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                          toolbar:
                            'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                          placeholder: 'Description (optional)',
                          spellchecker_dialog: true,
                          spellchecker_rpc_url: 'localhost/ephox-spelling',
                          spellchecker_language: 'en_US',
                          spellchecker_active: true,
                          browser_spellcheck: true,
                          contextmenu: false,
                        }}
                      />
                      {Boolean(errors?.s_description?.message) && (
                        <Typography color="error">
                          {errors?.s_description?.message}
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.status)} sx={{ width: { xs: '100%', sm: '70%' } }}>
                        <InputLabel id="position-status-label" size="small">
                          Position Status
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="position-status-label"
                          label="Position Status"
                          size="small"
                        >
                          {jobPostingStatuses.map(({ status, label }) => (
                            <MenuItem key={status} value={status}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors?.status && (
                          <FormHelperText>
                            {errors?.status.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Stack
                    mt={1}
                    spacing={2}
                    direction="row"
                    alignItems="center"
                    justifyContent="end"
                    sx={{ width: { xs: '100%', sm: '30%' } }}
                  >
                    <Button
                      sx={{ width: '100%' }}
                      variant="outlined"
                      size="small"
                      onClick={() => setShowAiPromptModal(true)}
                      startIcon={<AssistantIcon />}
                    >
                      Write with AI
                    </Button>
                    <Tooltip title={AITooltipText} placement="top" arrow>
                      <HelpOutline color="secondary" fontSize="small" />
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <Controller
                    control={control}
                    name="s_type"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.s_type)} fullWidth>
                        <InputLabel id="position-type-label" size="small">
                          Position Type
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="position-type-label"
                          label="Position Type"
                          size="small"
                        >
                          {updatedPositionTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors?.s_type && (
                          <FormHelperText>
                            {errors?.s_type.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="job_location"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.job_location)} fullWidth>
                        <InputLabel id="job_location-type-label" size="small">
                          Job Location Type
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="job_location"
                          label="Job Location Type"
                          size="small"
                        >
                         
                            <MenuItem value={0}>
                            In person
                            </MenuItem>
                            <MenuItem value={1}>
                            Remote 
                            </MenuItem>
                        </Select>
                        {errors?.s_type && (
                          <FormHelperText>
                            {errors?.s_type.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Stack>

                <Stack
                  spacing={{ xs: 0, sm: 3 }}
                  direction={{ xs: 'column', sm: 'row' }}
                >
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Tooltip
                      title="Adding compensation is mandatory for many of the job boards including Zip and Indeed, and will help your jobs get seen more! Use a single rate or range if your pay rate is negotiable based on experience."
                      placement="right"
                      arrow
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </Tooltip>
                    <Typography>Compensation</Typography>
                  </Stack>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={isPayRange} />}
                    label="Use a range"
                    onChange={() => setIsPayRange(!isPayRange)}
                    sx={{ marginLeft: '0 !important', whiteSpace: 'nowrap' }}
                  />

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems="center"
                  >
                    <Controller
                      control={control}
                      name="s_salary"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          type="text"
                          size="small"
                          fullWidth
                          sx={{ maxWidth: '120px' }}
                          placeholder="0.00"
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw.replace(/[^0-9.]/g, '');
                            const parts = cleaned.split('.');
                            const limited =
                              parts.length > 2
                                ? parts[0] + '.' + parts.slice(1).join('')
                                : cleaned;
                            const maxLength = limited.includes('.') ? 7 : 6;
                            if (limited.length <= maxLength) {
                              field.onChange(limited || null);
                            }
                          }}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              'Backspace',
                              'Tab',
                              'ArrowLeft',
                              'ArrowRight',
                              'Delete',
                              '.',
                            ];
                            if (
                              !/^\d$/.test(e.key) &&
                              !allowedKeys.includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                            if (
                              e.key === '.' &&
                              String(field.value ?? '').includes('.')
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            const pasted = e.clipboardData.getData('Text');
                            if (!/^\d*\.?\d*$/.test(pasted)) {
                              e.preventDefault();
                            }
                          }}
                          InputProps={{
                            inputMode: 'decimal',
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                    {isPayRange && (
                      <>
                        <Typography>-</Typography>
                        <Controller
                          control={control}
                          name="s_salary_max"
                          render={({ field }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              type="text"
                              size="small"
                              fullWidth
                              sx={{ maxWidth: '120px' }}
                              placeholder="0.00"
                              onChange={(e) => {
                                const raw = e.target.value;
                                const cleaned = raw.replace(/[^0-9.]/g, '');
                                const parts = cleaned.split('.');
                                const limited =
                                  parts.length > 2
                                    ? parts[0] + '.' + parts.slice(1).join('')
                                    : cleaned;
                                const maxLength = limited.includes('.') ? 7 : 6;
                                if (limited.length <= maxLength) {
                                  field.onChange(limited || null);
                                }
                              }}
                              onKeyDown={(e) => {
                                const allowedKeys = [
                                  'Backspace',
                                  'Tab',
                                  'ArrowLeft',
                                  'ArrowRight',
                                  'Delete',
                                  '.',
                                ];
                                if (
                                  !/^\d$/.test(e.key) &&
                                  !allowedKeys.includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                                if (
                                  e.key === '.' &&
                                  String(field.value ?? '').includes('.')
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                const pasted = e.clipboardData.getData('Text');
                                if (!/^\d*\.?\d*$/.test(pasted)) {
                                  e.preventDefault();
                                }
                              }}
                              InputProps={{
                                inputMode: 'decimal',
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AttachMoneyIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </>
                    )}
                    <Controller
                      control={control}
                      name="s_pay_frequency"
                      render={({ field }) => (
                        <FormControl
                          sx={{ width: '144px' }}
                          error={Boolean(errors.s_pay_frequency)}
                          fullWidth
                        >
                          <InputLabel id="pay-frequency-label" size="small">
                            Pay Frequency
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="pay-frequency-label"
                            label="Pay Frequency"
                            size="small"
                          >
                            {payFrequencyTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors?.s_pay_frequency && (
                            <FormHelperText error>
                              {errors?.s_pay_frequency.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Stack>
                </Stack>
                <Grid container justifyContent="space-between">
                  {checkBoxOptions.map((option) => (
                    <Grid item xs={6} sm="auto">
                      <Controller
                        key={option.name}
                        control={control}
                        name={option.name}
                        render={({ field: { value, onChange } }) => (
                          <FormControlLabel
                            sx={{ width: '100%' }}
                            control={
                              <Checkbox
                                checked={value}
                                onChange={(e) => onChange(e.target.checked)}
                              />
                            }
                            label={option.label}
                          />
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>

                <LoadingButton
                  type="submit"
                  startIcon={<DoneIcon />}
                  variant="contained"
                  size="small"
                  fullWidth
                  loading={isAddingJobPosting || isUpdatingJobPosting}
                  loadingPosition="start"
                >
                  Save
                </LoadingButton>
              </Stack>
            </form>
          </Stack>
        </DialogContent>
      </Dialog>
      <RequestPositionModal
        isOpen={isRequestPositionModalOpen}
        handleClose={() => setIsRequestPositionModalOpen(false)}
      />
      {noCompensionWarning && (
        <NoCompensationWarningModal
          open={noCompensionWarning}
          handleEdit={() => {
            setNoCompensionWarning(false);
          }}
          handleSave={() => {
            setNoCompensionWarning(false);
            onSubmit(watch());
          }}
        />
      )}
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </>
  );
};

export default TemplateJobPostingModal;
